/**
 * Route poster v2 trip form (sections/tn-rp2-form.liquid).
 *
 * A copy of assets/tn-quick-form.js (left untouched) with these changes:
 *   - the package (Free preview / Partial pay / Designed & framed) and the
 *     variant come from window.TNRP2 (assets/tn-rp2.js), not the theme's
 *     variant picker, and the form has its own submit button;
 *   - "Who is it for?" / "When do you need it?" tap choices, sent to the
 *     Studio in the note and added to the cart line as properties;
 *   - Free preview: the trip goes to the Studio intake only. No cart, no
 *     checkout, so Meta never sees a zero-value Purchase. A Lead event fires
 *     once, after the Studio has accepted the trip;
 *   - no global Add to cart takeover (this template has no buy buttons).
 * The Studio API contract is the same; see blocks/tn-quick-form.liquid.
 *
 * Plain ES2019+, no build step, no dependencies. heic-to (a current libheif)
 * is fetched from jsDelivr only when an iPhone HEIC photo is picked and the
 * browser cannot decode it itself.
 */
(function () {
  if (window.__tnRp2Form) return;
  window.__tnRp2Form = true;

  var WHATSAPP_HELP = 'https://wa.me/918089611397';
  var HEIC_SRC = 'https://cdn.jsdelivr.net/npm/heic-to@1.5.2/dist/iife/heic-to.js';
  var MAX_EDGE = 4000;
  var JPEG_QUALITY = 0.9;
  var MAX_STOPS = 25;

  /* The poster's photo is portrait: every photo is framed at 3:4 before it uploads. */
  var CROP_W = 3;
  var CROP_H = 4;
  var MAX_ZOOM = 4;

  /**
   * What a pair of grades means for the sizes the shop sells. L is never
   * better than M (the same pixels over a bigger sheet), so the pairs read
   * worst-last; `rank` orders them, to tell when zooming cost a grade.
   */
  function cropVerdict(m, l) {
    if (m === 'green' && l === 'green') return { tone: 'green', rank: 0, text: 'Perfect for M and L: sharp at both sizes.' };
    if (m === 'green' && l === 'amber') return { tone: 'green', rank: 1, text: 'Perfect for M, and good for L.' };
    if (m === 'green') return { tone: 'amber', rank: 2, text: 'Perfect for M. For L, a larger photo would print sharper.' };
    if (m === 'amber' && l === 'amber') return { tone: 'amber', rank: 3, text: 'Good for M and L, a little soft up close.' };
    if (m === 'amber') return { tone: 'amber', rank: 4, text: 'Good for M. Too small for a sharp L.' };
    return { tone: 'red', rank: 5, text: 'This may print blurry. Zoom out, or pick a larger photo.' };
  }
  var FIRST_STOP_PLACEHOLDER = 'Where your journey began';

  /*
   * The optional questions, per trip type: the Studio's Expedition style's own
   * ids (studio-toponomad src/lib/intake/types.ts INTAKE_FIELDS). The Studio
   * refuses an id not on its list, so the two must move together.
   */
  var OPTIONAL_FIELDS = {
    trek: ['subtitle', 'distance_km', 'highest_altitude', 'food', 'stays', 'unforgettable', 'reflection', 'weather', 'trip_rating', 'travelled_with'],
    road_trip: ['subtitle', 'vehicle', 'distance_km', 'highest_altitude', 'food', 'stays', 'unforgettable', 'reflection', 'weather', 'trip_rating'],
    marathon: ['subtitle', 'finish_time', 'pace', 'distance_km', 'bib_number', 'unforgettable', 'reflection', 'weather', 'trip_rating', 'travelled_with'],
    travelogue: [
      'subtitle', 'region', 'season', 'countries', 'days', 'nights', 'airline', 'flight_route', 'flight_time', 'base_city',
      'distance_km', 'highest_altitude', 'transport', 'food', 'stays', 'highlights', 'activities', 'unforgettable',
      'reflection', 'weather', 'trip_rating', 'travelled_with',
    ],
  };
  /* The Studio's cap on a GPX upload (MAX_GPX_UPLOAD_BYTES in src/lib/route-gpx.ts). */
  var MAX_GPX_BYTES = 10 * 1024 * 1024;
  var STORAGE_PREFIX = 'tn_rp2_form_v1_';
  /* The Studio's names for the packages (INTAKE_PACKAGES in studio-toponomad src/lib/intake/draft-due.ts). */
  var PACKAGE_IDS = { free: 'free_preview', priority: 'priority', framed: 'framed' };
  var DEFAULT_ORIGIN = 'https://studio.toponomad.co';


  var ISSUE_TEXT = {
    required: 'This is required.',
    too_long: 'That is too long.',
    bad_date: 'Please pick a date.',
    bad_number: 'Please enter a number.',
    end_before_start: 'End date is before the start date.',
    unknown_field: 'Please check this.',
    bad_rating: 'Pick 1 to 5 stars.',
    bad_gpx: 'Please add your GPX file again.',
    gpx_expired: 'Please add your GPX file again.',
  };

  /* ------------------------------------------------------------------ */
  /* Small helpers                                                       */
  /* ------------------------------------------------------------------ */

  function readCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      var index = parts[i].indexOf('=');
      if (index > 0 && parts[i].slice(0, index) === name) {
        try {
          return decodeURIComponent(parts[i].slice(index + 1));
        } catch (e) {
          return parts[i].slice(index + 1);
        }
      }
    }
    return '';
  }

  /** utm_*, Meta click ids and the page, the same way tn-studio-link.js reads them. */
  function trackingSource() {
    var out = {};
    try {
      var search = new URLSearchParams(window.location.search);
      search.forEach(function (value, key) {
        if (key.indexOf('utm_') === 0 && value) out[key] = value.slice(0, 500);
      });
      var fbp = readCookie('_fbp');
      if (fbp) out.fbp = fbp.slice(0, 500);
      var fbc = readCookie('_fbc');
      var fbclid = search.get('fbclid');
      if (fbc) out.fbc = fbc.slice(0, 500);
      else if (fbclid) out.fbc = ('fb.1.' + Date.now() + '.' + fbclid).slice(0, 500);
      out.page = window.location.href.slice(0, 500);
    } catch (e) {
      /* attribution is never worth a failed order */
    }
    return out;
  }

  function isValidPhone(raw) {
    var v = (raw || '').trim();
    return /^\d{10}$/.test(v) || /^\+\d{8,15}$/.test(v);
  }

  /**
   * HEIC by type, by name, or by the bytes: files shared out of WhatsApp or
   * Google Photos often arrive with no type, or named .jpg while still HEIC.
   */
  function isHeic(file) {
    if (!file) return Promise.resolve(false);
    if (/^image\/hei[cf](-sequence)?$/i.test(file.type || '')) return Promise.resolve(true);
    if (/\.hei[cf]$/i.test(file.name || '')) return Promise.resolve(true);
    if (!file.slice || !file.arrayBuffer) return Promise.resolve(false);
    return file
      .slice(4, 12)
      .arrayBuffer()
      .then(function (buf) {
        var tag = String.fromCharCode.apply(null, new Uint8Array(buf));
        return /^ftyp(heic|heix|hevc|hevx|heim|heis|mif1|msf1)$/.test(tag);
      })
      .catch(function () {
        return false;
      });
  }

  var heicPromise = null;
  function loadHeic() {
    if (window.HeicTo) return Promise.resolve(window.HeicTo);
    if (heicPromise) return heicPromise;
    heicPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = HEIC_SRC;
      script.async = true;
      script.onload = function () {
        if (window.HeicTo) resolve(window.HeicTo);
        else reject(new Error('heic'));
      };
      script.onerror = function () {
        heicPromise = null;
        reject(new Error('heic'));
      };
      document.head.appendChild(script);
    });
    return heicPromise;
  }

  /**
   * Decode upright. createImageBitmap honours EXIF with imageOrientation
   * 'from-image'; where that option is missing, an <img> does (every current
   * browser applies EXIF orientation to images by default).
   */
  function decodeImage(blob) {
    var viaImg = function () {
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(blob);
        var img = new Image();
        img.onload = function () {
          resolve({
            source: img,
            width: img.naturalWidth,
            height: img.naturalHeight,
            close: function () {
              URL.revokeObjectURL(url);
            },
          });
        };
        img.onerror = function () {
          URL.revokeObjectURL(url);
          reject(new Error('decode'));
        };
        img.src = url;
      });
    };
    if (typeof window.createImageBitmap !== 'function') return viaImg();
    return window
      .createImageBitmap(blob, { imageOrientation: 'from-image' })
      .then(function (bitmap) {
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          close: function () {
            if (bitmap.close) bitmap.close();
          },
        };
      })
      .catch(viaImg);
  }

  /**
   * Decode the pick. Safari (and Chrome on recent macOS) decode HEIC natively,
   * so that is tried first; only a HEIC the browser refuses goes through
   * heic-to, which is ~3 MB and worth skipping when it can be.
   */
  function decodePick(file, onStage) {
    return decodeImage(file).catch(function (nativeErr) {
      return isHeic(file).then(function (heic) {
        if (!heic) throw nativeErr;
        onStage('Converting your iPhone photo…');
        return loadHeic()
          .then(function (heicTo) {
            return heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
          })
          .then(decodeImage);
      });
    });
  }

  function readJson(response) {
    return response.json().catch(function () {
      return null;
    });
  }

  /**
   * A Studio without the package fields (not yet deployed, or rolled back)
   * refuses them as unknown keys. Then the same trip goes again the way the
   * first version of this form sent it: the three answers lead the designer's
   * note ("Package: Free preview", …). Null for any other refusal.
   */
  var NEW_KEYS = ['package', 'forWhom', 'whenNeeded'];
  function legacyBody(body, error) {
    var issues = error && error.status === 400 && error.json && error.json.issues;
    if (!Array.isArray(issues) || !issues.length) return null;
    var aboutNewKeys = issues.every(function (issue) {
      return NEW_KEYS.indexOf(String(issue && issue.path).split('.')[0]) !== -1;
    });
    if (!aboutNewKeys) return null;
    var names = { free_preview: 'Free preview', priority: 'Partial pay', framed: 'Designed & framed' };
    var lines = ['Package: ' + (names[body.package] || body.package)];
    if (body.forWhom) lines.push('Who is it for?: ' + body.forWhom);
    if (body.whenNeeded) lines.push('When do you need it?: ' + body.whenNeeded);
    var legacy = Object.assign({}, body);
    NEW_KEYS.forEach(function (key) {
      delete legacy[key];
    });
    legacy.notes = (lines.join('\n') + (body.notes ? '\n\n' + body.notes : '')).slice(0, 1000);
    return legacy;
  }

  /* ------------------------------------------------------------------ */
  /* Places autocomplete, one per input                                  */
  /* ------------------------------------------------------------------ */

  var placeCounter = 0;

  function Autocomplete(form, input) {
    this.form = form;
    this.input = input;
    this.place = null; /* {name, lngLat?, context?} once picked */
    this.results = [];
    this.active = -1;
    this.timer = null;
    this.controller = null;
    this.lastQuery = '';

    placeCounter += 1;
    var listId = 'tnq-places-' + placeCounter;
    this.list = document.createElement('ul');
    this.list.className = 'tn-quick-form__options';
    this.list.id = listId;
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;
    input.parentNode.appendChild(this.list);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);

    var self = this;
    input.addEventListener('input', function () {
      self.onInput();
    });
    input.addEventListener('keydown', function (event) {
      self.onKeydown(event);
    });
    input.addEventListener('blur', function () {
      setTimeout(function () {
        self.close();
      }, 150);
    });
    this.list.addEventListener('mousedown', function (event) {
      event.preventDefault(); /* keep focus in the input */
    });
    this.list.addEventListener('click', function (event) {
      var li = event.target.closest('[data-index]');
      if (li) self.pick(Number(li.getAttribute('data-index')));
    });
  }

  Autocomplete.prototype.value = function () {
    var name = this.input.value.trim().slice(0, 120);
    if (!name) return null;
    if (this.place && this.place.name === name) return this.place;
    return { name: name };
  };

  Autocomplete.prototype.setPlace = function (place) {
    if (!place || !place.name) return;
    this.input.value = place.name;
    this.place = place.lngLat ? place : null;
  };

  Autocomplete.prototype.onInput = function () {
    var q = this.input.value.trim();
    if (this.place && this.place.name !== q) this.place = null;
    clearTimeout(this.timer);
    if (q.length < 2) {
      this.close();
      return;
    }
    var self = this;
    this.timer = setTimeout(function () {
      self.search(q);
    }, 300);
  };

  Autocomplete.prototype.search = function (q) {
    var form = this.form;
    if (Date.now() < form.placesPausedUntil) return;
    var cached = form.placesCache.get(q.toLowerCase());
    if (cached) {
      this.render(cached);
      return;
    }
    if (this.controller) this.controller.abort();
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    this.controller = controller;
    this.lastQuery = q;
    var self = this;
    fetch(form.origin + '/api/intake/places?q=' + encodeURIComponent(q), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    })
      .then(function (response) {
        if (response.status === 429) {
          form.placesPausedUntil = Date.now() + 30000;
          return null;
        }
        if (!response.ok) return null;
        return readJson(response);
      })
      .then(function (json) {
        if (!json || !Array.isArray(json.results)) return;
        var results = json.results
          .filter(function (r) {
            return r && typeof r.name === 'string' && r.name.trim();
          })
          .slice(0, 8);
        form.placesCache.set(q.toLowerCase(), results);
        if (self.lastQuery === q && self.input.value.trim() === q) self.render(results);
      })
      .catch(function () {
        /* offline, aborted or blocked: free text still works */
      });
  };

  Autocomplete.prototype.render = function (results) {
    this.results = results;
    this.active = -1;
    this.list.textContent = '';
    if (!results.length || document.activeElement !== this.input) {
      this.close();
      return;
    }
    var listId = this.list.id;
    results.forEach(function (r, i) {
      var li = document.createElement('li');
      li.className = 'tn-quick-form__option';
      li.id = listId + '-' + i;
      li.setAttribute('role', 'option');
      li.setAttribute('data-index', String(i));
      var name = document.createElement('span');
      name.className = 'tn-quick-form__option-name';
      name.textContent = r.name;
      li.appendChild(name);
      if (r.context) {
        var ctx = document.createElement('span');
        ctx.className = 'tn-quick-form__option-context';
        ctx.textContent = r.context;
        li.appendChild(ctx);
      }
      this.list.appendChild(li);
    }, this);
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  };

  Autocomplete.prototype.close = function () {
    this.list.hidden = true;
    this.active = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  };

  Autocomplete.prototype.highlight = function (index) {
    var items = this.list.querySelectorAll('[role="option"]');
    if (!items.length) return;
    this.active = (index + items.length) % items.length;
    for (var i = 0; i < items.length; i++) {
      items[i].setAttribute('aria-selected', i === this.active ? 'true' : 'false');
    }
    this.input.setAttribute('aria-activedescendant', items[this.active].id);
  };

  Autocomplete.prototype.onKeydown = function (event) {
    var open = !this.list.hidden;
    if (event.key === 'ArrowDown' && open) {
      event.preventDefault();
      this.highlight(this.active + 1);
    } else if (event.key === 'ArrowUp' && open) {
      event.preventDefault();
      this.highlight(this.active - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && this.active >= 0) this.pick(this.active);
      else this.close();
    } else if (event.key === 'Escape' && open) {
      this.close();
    }
  };

  Autocomplete.prototype.pick = function (index) {
    var r = this.results[index];
    if (!r) return;
    var place = { name: String(r.name).trim().slice(0, 120) };
    if (
      Array.isArray(r.lngLat) &&
      r.lngLat.length === 2 &&
      isFinite(r.lngLat[0]) &&
      isFinite(r.lngLat[1])
    ) {
      place.lngLat = [Number(r.lngLat[0]), Number(r.lngLat[1])];
    }
    if (r.context) place.context = String(r.context).trim().slice(0, 200);
    this.input.value = place.name;
    this.place = place;
    this.close();
    this.form.clearError(this.input);
    this.form.scheduleSave();
  };

  /* ------------------------------------------------------------------ */
  /* The form                                                            */
  /* ------------------------------------------------------------------ */

  class TnRp2Form extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.origin = DEFAULT_ORIGIN;
      try {
        this.origin = new URL(this.dataset.origin || DEFAULT_ORIGIN).origin;
      } catch (e) {
        this.origin = DEFAULT_ORIGIN;
      }
      var category = this.dataset.category;
      this.category = category === 'road_trip' || category === 'marathon' || category === 'travelogue' ? category : 'trek';
      /* A marathon product's mapped course (the Studio preset id), when it has one. */
      this.presetId = this.dataset.presetId || '';
      this.courseName = this.dataset.courseName || '';
      this.storageKey = STORAGE_PREFIX + (this.dataset.productId || 'default');

      this.placesCache = new Map();
      this.placesPausedUntil = 0;
      this.stops = [];
      this.photo = { state: 'idle', blob: null, photoId: '', promise: null, generation: 0 };
      this.gpx = { state: 'idle', gpxId: '', name: '', km: 0, generation: 0 };
      this.submitting = false;
      this.lastIntake = null;
      this.saveTimer = null;

      this.banner = this.querySelector('[data-tnq-banner]');
      this.progress = this.querySelector('[data-tnq-progress]');
      this.stopsList = this.querySelector('[data-tnq-stops]');
      this.stopTemplate = this.querySelector('[data-tnq-stop-template]');
      this.addStopButton = this.querySelector('[data-tnq-add-stop]');
      this.more = this.querySelector('[data-tnq-more]');
      this.honeypot = this.querySelector('[data-tnq-hp]');

      this.copy = {};
      try {
        var copyTag = this.querySelector('[data-tn-rp2-form-copy]');
        this.copy = JSON.parse((copyTag && copyTag.textContent) || '{}') || {};
      } catch (e) {
        this.copy = {};
      }
      if (this.dataset.helpNumber) WHATSAPP_HELP = 'https://wa.me/' + this.dataset.helpNumber;
      this.body = this.querySelector('.tn-quick-form__body');
      this.submitButton = this.querySelector('[data-tnq-submit]');
      this.freeNote = this.querySelector('[data-tn-rp2-free-note]');
      this.confirm = this.querySelector('[data-tn-rp2-confirm]');
      this.neededBy = this.querySelector('[data-tn-rp2-needed-by]');
      this.leadSent = {};
      this.freeIntake = null;
      /* "options_after": the form saves the trip as a lead, then the options screen picks the package. */
      this.flow = this.dataset.flow === 'options_after' ? 'options' : 'package';
      this.header = this.querySelector('.tn-quick-form__header');
      this.options = this.querySelector('[data-tn-rp2-options]');
      this.lead = null;
      this.tracked = {};

      this.setupPhoto();
      this.setupGpx();
      this.setupStops();
      this.setupWhatsApp();
      this.setupRating();
      this.setupPackage();
      this.setupOptions();

      this.addEventListener('input', (event) => {
        this.track('rp2_form_started');
        this.clearError(event.target);
        this.scheduleSave();
      });
      this.addEventListener('change', (event) => {
        var target = event.target;
        if (target && target.matches && target.matches('[data-tnq-choice]')) {
          this.clearError(target);
          this.syncNeededBy();
        }
        if (target && target.matches && target.matches('[data-tnq-input="end_date"], [data-tnq-input="start_date"]')) this.syncFreeNote();
        this.scheduleSave();
      });
      if (this.more) this.more.addEventListener('toggle', () => this.scheduleSave());

      this.restore();
      if (!this.stops.length && this.stopsList) this.addStop();
      this.paintRating();
      this.syncNeededBy();
      this.syncPackage();
    }

    /* ------------------------ package (tn-rp2.js) ------------------------ */

    rp2() {
      return window.TNRP2 || null;
    }

    pkg() {
      var rp2 = this.rp2();
      return (rp2 && rp2.state.pkg) || 'priority';
    }

    setupPackage() {
      if (this.submitButton) this.submitButton.addEventListener('click', () => this.submit());
      this.querySelectorAll('[data-tn-rp2-switch="priority"]').forEach((button) => {
        button.addEventListener('click', () => this.switchToPriority());
      });
      var upgrade = this.querySelector('[data-tn-rp2-upgrade]');
      if (upgrade) upgrade.addEventListener('click', () => this.upgradeFromConfirm());
      document.addEventListener('tn-rp2:change', () => this.syncPackage());
      /* tn-rp2.js may load after this script: pick its state up when it is ready. */
      document.addEventListener('tn-rp2:ready', () => this.syncPackage());
    }

    /** Button label, hints, the cutoff line and the free-preview note follow the package. */
    syncPackage() {
      if (this.flow === 'options') return this.syncCreate();
      var pkg = this.pkg();
      var rp2 = this.rp2();
      var variant = rp2 ? rp2.variant(rp2.state.variantId) : null;
      var price = variant ? variant.priceText : '';
      var label = this.copy.submitPriority || '';
      if (pkg === 'free') label = this.copy.submitFree || '';
      else if (pkg === 'framed') label = this.copy.submitFramed || '';
      label = label.replace('[price]', price).replace(/\s*·\s*$/, '').trim();
      this.submitLabel = label;
      if (this.submitButton && !this.submitting) this.submitButton.textContent = label;

      this.querySelectorAll('[data-tn-rp2-free-only]').forEach(function (el) {
        el.hidden = pkg !== 'free';
      });
      this.querySelectorAll('[data-tn-rp2-paid-only]').forEach(function (el) {
        el.hidden = pkg === 'free';
      });
      if (rp2) rp2.refreshCutoff();
      this.syncQuestions();
      this.syncFreeNote();
    }

    /** Form-first flow: one button whatever the package, and the free-to-start line. */
    syncCreate() {
      this.submitLabel = this.copy.submitCreate || 'Create my poster';
      if (this.submitButton && !this.submitting) this.submitButton.textContent = this.submitLabel;
      this.querySelectorAll('[data-tn-rp2-free-only], [data-tn-rp2-paid-only]').forEach(function (el) {
        el.hidden = true;
      });
      this.querySelectorAll('[data-tn-rp2-create-only]').forEach(function (el) {
        el.hidden = false;
      });
      if (this.freeNote) this.freeNote.hidden = true;
      var rp2 = this.rp2();
      if (rp2) rp2.refreshCutoff();
      this.syncQuestions();
      if (this.lead) this.fillOptions();
    }

    /** True when the trip's end date is older than the free-preview window. */
    freeTooOld() {
      /* A race has one date: its day stands in for the end. */
      var end = this.value('end_date') || this.value('start_date');
      var rp2 = this.rp2();
      if (!end || !rp2) return false;
      var days = Number(rp2.config.freeDays) || 90;
      return rp2.daysAgo(end) > days;
    }

    syncFreeNote() {
      if (!this.freeNote || this.flow === 'options') return;
      this.freeNote.hidden = !(this.pkg() === 'free' && this.freeTooOld());
    }

    switchToPriority() {
      var rp2 = this.rp2();
      if (rp2) rp2.selectPackage('priority', { scroll: false });
      this.syncPackage();
      if (this.submitButton) {
        this.submitButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => this.submitButton.focus({ preventScroll: true }), 350);
      }
    }

    /** From the confirmation panel: back to the form as it was, on Priority. */
    upgradeFromConfirm() {
      if (this.confirm) this.confirm.hidden = true;
      if (this.flow === 'options' && this.lead) return this.showOptions();
      if (this.body) this.body.hidden = false;
      this.switchToPriority();
    }

    /* ---------------------------- choices ---------------------------- */

    /** Whether the chosen package asks this question (theme editor "Ask ... for" checkboxes). */
    asked(key) {
      var field = this.querySelector('[data-tnq-field="' + key + '"]');
      if (!field) return false;
      if (!field.hasAttribute('data-tn-rp2-for')) return true;
      return field.getAttribute('data-tn-rp2-for').split(',').indexOf(this.pkg()) !== -1;
    }

    /** The picked answer, or '' when the package does not ask the question. */
    choice(key) {
      if (!this.asked(key)) return '';
      var checked = this.querySelector('[data-tnq-choice="' + key + '"]:checked');
      return checked ? checked.value : '';
    }

    syncQuestions() {
      ['who', 'when'].forEach((key) => {
        var field = this.querySelector('[data-tnq-field="' + key + '"]');
        if (field) field.hidden = !this.asked(key);
      });
      this.syncNeededBy();
    }

    needsDate() {
      if (!this.asked('when')) return false;
      var checked = this.querySelector('[data-tnq-choice="when"]:checked');
      return !!(checked && checked.hasAttribute('data-tn-rp2-needs-date'));
    }

    syncNeededBy() {
      if (this.neededBy) this.neededBy.hidden = !this.needsDate();
    }

    /** "By a specific date (2026-10-20)", or the plain choice. */
    whenText() {
      var when = this.choice('when');
      if (when && this.needsDate() && this.value('needed_by')) when += ' (' + this.value('needed_by') + ')';
      return when;
    }

    input(key) {
      return this.querySelector('[data-tnq-input="' + key + '"]');
    }

    value(key) {
      var el = this.input(key);
      return el ? el.value.trim() : '';
    }

    /* ---------------------------- photo ---------------------------- */

    setupPhoto() {
      this.photoInput = this.querySelector('[data-tnq-photo]');
      this.photoButton = this.querySelector('[data-tnq-photo-button]');
      this.photoButtonText = this.photoButton ? this.photoButton.textContent : '';
      this.photoStatus = this.querySelector('[data-tnq-photo-status]');
      this.thumbWrap = this.querySelector('[data-tnq-thumb-wrap]');
      this.thumb = this.querySelector('[data-tnq-thumb]');
      this.badge = this.querySelector('[data-tnq-badge]');
      this.detail = this.querySelector('[data-tnq-detail]');
      this.redWarning = this.querySelector('[data-tnq-red]');
      this.retryButton = this.querySelector('[data-tnq-retry]');
      this.adjustButton = this.querySelector('[data-tnq-adjust]');

      if (this.photoInput) {
        this.photoInput.addEventListener('change', () => {
          var file = this.photoInput.files && this.photoInput.files[0];
          if (file) this.pickPhoto(file);
        });
      }
      if (this.retryButton) {
        this.retryButton.addEventListener('click', () => {
          if (this.photo.blob) this.uploadPhoto();
          else if (this.photoInput) this.photoInput.click();
        });
      }
      if (this.adjustButton) {
        this.adjustButton.addEventListener('click', () => {
          if (this.crop) this.openCrop();
        });
      }
      this.setupCrop();
    }

    setPhotoStatus(text, tone) {
      if (!this.photoStatus) return;
      this.photoStatus.textContent = text || '';
      this.photoStatus.className = 'tn-quick-form__status' + (tone ? ' is-' + tone : '');
    }

    resetVerdict() {
      if (this.badge) this.badge.hidden = true;
      if (this.detail) this.detail.hidden = true;
      if (this.redWarning) this.redWarning.hidden = true;
      if (this.retryButton) this.retryButton.hidden = true;
    }

    /**
     * Pick -> decode (converting HEIC) -> the framing window. Nothing uploads
     * until the customer has framed it: the upload is the 3:4 crop.
     */
    pickPhoto(file) {
      var generation = ++this.photo.generation;
      if (this.photo.controller) this.photo.controller.abort();
      this.photo.blob = null;
      this.photo.photoId = '';
      this.photo.state = 'processing';
      this.photo.promise = null;
      this.resetVerdict();
      this.clearError(this.photoInput);
      this.setPhotoStatus('Preparing your photo…');
      this.loadSpec();

      decodePick(file, (text) => {
        if (generation === this.photo.generation) this.setPhotoStatus(text);
      }).then(
        (image) => {
          if (generation !== this.photo.generation) {
            image.close();
            return;
          }
          if (this.crop && this.crop.image) this.crop.image.close();
          this.crop = {
            image: image,
            zoom: 1,
            cx: image.width / 2,
            cy: image.height / 2,
            /* The widest 3:4 crop the photo allows: its width at zoom 1. */
            base: Math.min(image.width, (image.height * CROP_W) / CROP_H),
          };
          this.photo.state = 'cropping';
          this.setPhotoStatus('');
          if (this.photoButton) this.photoButton.textContent = 'Change photo';
          this.openCrop();
        },
        () => {
          if (generation !== this.photo.generation) return;
          this.photo.state = 'error';
          this.setPhotoStatus('We could not read that photo. Try a JPG, PNG or HEIC.', 'error');
          if (this.photoInput) this.photoInput.value = '';
        }
      );
    }

    /* ------------------------- framing (3:4) ------------------------ */

    /** How many source pixels a 3:4 crop needs, from the Studio (null until it answers). */
    loadSpec() {
      if (this.specPromise) return this.specPromise;
      this.specPromise = fetch(this.origin + '/api/intake/photo?category=' + encodeURIComponent(this.category), {
        mode: 'cors',
        credentials: 'omit',
      })
        .then((response) => (response.ok ? readJson(response) : null))
        .then((json) => {
          this.spec = json && json.need && json.need.M && json.need.L ? json : null;
          if (this.cropOpen) this.paintQuality();
          return this.spec;
        })
        .catch(() => {
          this.specPromise = null;
          return null;
        });
      return this.specPromise;
    }

    setupCrop() {
      this.cropModal = this.querySelector('[data-tnq-crop]');
      if (!this.cropModal) return;
      this.cropStage = this.cropModal.querySelector('[data-tnq-crop-stage]');
      this.cropCanvas = this.cropModal.querySelector('[data-tnq-crop-canvas]');
      this.zoomInput = this.cropModal.querySelector('[data-tnq-zoom]');
      this.zoomValue = this.cropModal.querySelector('[data-tnq-zoom-value]');
      this.qualityLine = this.cropModal.querySelector('[data-tnq-quality]');
      this.cropDone = this.cropModal.querySelector('[data-tnq-crop-done]');
      this.pointers = new Map();
      this.onCropResize = () => {
        if (this.cropOpen) this.drawCrop();
      };

      this.cropModal.querySelectorAll('[data-tnq-crop-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeCrop(false));
      });
      if (this.cropDone) this.cropDone.addEventListener('click', () => this.closeCrop(true));
      if (this.zoomInput) {
        this.zoomInput.addEventListener('input', () => this.setZoom(Number(this.zoomInput.value)));
      }
      this.cropModal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeCrop(false);
        }
      });
      if (!this.cropStage) return;

      this.cropStage.addEventListener('keydown', (event) => {
        if (!this.crop) return;
        var step = 20 / this.cropScale();
        var moves = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
        if (moves[event.key]) {
          event.preventDefault();
          this.panBy(moves[event.key][0], moves[event.key][1]);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          this.setZoom(this.crop.zoom * 1.1);
        } else if (event.key === '-' || event.key === '_') {
          event.preventDefault();
          this.setZoom(this.crop.zoom / 1.1);
        }
      });
      this.cropStage.addEventListener('pointerdown', (event) => {
        if (this.cropStage.setPointerCapture) this.cropStage.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.cropStage.classList.add('is-dragging');
      });
      this.cropStage.addEventListener('pointermove', (event) => {
        var last = this.pointers.get(event.pointerId);
        if (!last || !this.crop) return;
        if (this.pointers.size === 1) {
          var scale = this.cropScale();
          this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          this.panBy((event.clientX - last.x) / scale, (event.clientY - last.y) / scale);
          return;
        }
        /* Two fingers: the change in their distance is the zoom. */
        var before = this.pinchDistance();
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        var after = this.pinchDistance();
        if (before > 0 && after > 0) this.setZoom(this.crop.zoom * (after / before));
      });
      var release = (event) => {
        this.pointers.delete(event.pointerId);
        if (!this.pointers.size) this.cropStage.classList.remove('is-dragging');
      };
      this.cropStage.addEventListener('pointerup', release);
      this.cropStage.addEventListener('pointercancel', release);
      this.cropStage.addEventListener(
        'wheel',
        (event) => {
          if (!this.crop) return;
          event.preventDefault();
          this.setZoom(this.crop.zoom * Math.exp(-event.deltaY * 0.0015));
        },
        { passive: false }
      );
    }

    pinchDistance() {
      var points = Array.from(this.pointers.values());
      if (points.length < 2) return 0;
      return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
    }

    /** The crop in source pixels: the zoom-1 width over the zoom, and 4/3 of that tall. */
    cropBox() {
      var c = this.crop;
      var w = c.base / c.zoom;
      var h = (w * CROP_H) / CROP_W;
      return { x: c.cx - w / 2, y: c.cy - h / 2, w: w, h: h };
    }

    /** Screen pixels per source pixel in the framing window. */
    cropScale() {
      var width = this.cropStage ? this.cropStage.clientWidth : 0;
      return width > 0 ? width / this.cropBox().w : 1;
    }

    clampCrop() {
      var c = this.crop;
      var box = this.cropBox();
      c.cx = Math.min(Math.max(c.cx, box.w / 2), c.image.width - box.w / 2);
      c.cy = Math.min(Math.max(c.cy, box.h / 2), c.image.height - box.h / 2);
    }

    panBy(dx, dy) {
      if (!this.crop) return;
      this.crop.cx -= dx;
      this.crop.cy -= dy;
      this.clampCrop();
      this.drawCrop();
    }

    setZoom(zoom) {
      if (!this.crop) return;
      this.crop.zoom = Math.min(MAX_ZOOM, Math.max(1, zoom || 1));
      this.clampCrop();
      if (this.zoomInput) this.zoomInput.value = String(this.crop.zoom);
      if (this.zoomValue) this.zoomValue.textContent = this.crop.zoom.toFixed(1) + '×';
      this.drawCrop();
      this.paintQuality();
    }

    drawCrop() {
      if (!this.crop || !this.cropCanvas || !this.cropStage) return;
      var ratio = window.devicePixelRatio || 1;
      var width = Math.max(1, Math.round(this.cropStage.clientWidth * ratio));
      var height = Math.max(1, Math.round((width * CROP_H) / CROP_W));
      if (this.cropCanvas.width !== width) this.cropCanvas.width = width;
      if (this.cropCanvas.height !== height) this.cropCanvas.height = height;
      var ctx = this.cropCanvas.getContext('2d');
      var box = this.cropBox();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(this.crop.image.source, box.x, box.y, box.w, box.h, 0, 0, width, height);
    }

    /** The live M / L line, from the Studio's thresholds and the crop's width in source pixels. */
    paintQuality() {
      if (!this.qualityLine || !this.crop) return;
      if (!this.spec) {
        this.qualityLine.hidden = true;
        return;
      }
      var spec = this.spec;
      var gradeAt = function (size, w) {
        var need = spec.need[size];
        return w >= need.green ? 'green' : w >= need.amber ? 'amber' : 'red';
      };
      var width = this.cropBox().w;
      var verdict = cropVerdict(gradeAt('M', width), gradeAt('L', width));
      var text = verdict.text;
      /* The zoom, not the photo, cost them a grade: say so, and how to fix it. */
      if (this.crop.zoom > 1.05) {
        var unzoomed = cropVerdict(gradeAt('M', this.crop.base), gradeAt('L', this.crop.base));
        if (unzoomed.rank < verdict.rank) text += ' Zoom out a little for a sharper print.';
      }
      this.qualityLine.textContent = text;
      this.qualityLine.className = 'tn-quick-form__quality is-' + verdict.tone;
      this.qualityLine.hidden = false;
    }

    openCrop() {
      if (!this.cropModal || !this.crop) return;
      /* Kept, so closing without "Use this photo" puts the last framing back. */
      this.cropBefore = { zoom: this.crop.zoom, cx: this.crop.cx, cy: this.crop.cy };
      if (this.cropModal.parentNode !== document.body) {
        this.cropHome = this.cropModal.parentNode;
        document.body.appendChild(this.cropModal);
      }
      this.cropReturnFocus = document.activeElement;
      this.cropModal.hidden = false;
      this.cropOpen = true;
      document.documentElement.classList.add('tnq-no-scroll');
      window.addEventListener('resize', this.onCropResize);
      this.setZoom(this.crop.zoom);
      if (this.cropStage) this.cropStage.focus({ preventScroll: true });
    }

    /**
     * "Use this photo" uploads the crop. Closing puts the last framing back;
     * on a first pick there is none yet, so the framing on screen is used —
     * the customer is never left with a photo that silently did not count.
     */
    closeCrop(confirmed) {
      if (!this.cropOpen) return;
      this.cropOpen = false;
      this.cropModal.hidden = true;
      this.pointers.clear();
      document.documentElement.classList.remove('tnq-no-scroll');
      window.removeEventListener('resize', this.onCropResize);
      if (this.cropHome) this.cropHome.appendChild(this.cropModal);
      if (this.cropReturnFocus && this.cropReturnFocus.focus) this.cropReturnFocus.focus({ preventScroll: true });

      if (!confirmed && this.photo.blob && this.cropBefore) {
        this.crop.zoom = this.cropBefore.zoom;
        this.crop.cx = this.cropBefore.cx;
        this.crop.cy = this.cropBefore.cy;
        return;
      }
      this.confirmCrop();
    }

    /** Render the crop at full resolution (long edge <= MAX_EDGE), then upload it. */
    confirmCrop() {
      var generation = ++this.photo.generation;
      if (this.photo.controller) this.photo.controller.abort();
      this.photo.state = 'processing';
      this.resetVerdict();
      this.setPhotoStatus('Preparing your photo…');
      var box = this.cropBox();
      var scale = Math.min(1, MAX_EDGE / box.h);
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(box.w * scale));
      canvas.height = Math.max(1, Math.round(box.h * scale));
      var ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.crop.image.source, box.x, box.y, box.w, box.h, 0, 0, canvas.width, canvas.height);

      this.photo.promise = new Promise(function (resolve, reject) {
        canvas.toBlob(
          function (out) {
            canvas.width = 0;
            canvas.height = 0;
            if (out) resolve(out);
            else reject(new Error('encode'));
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      }).then(
        (blob) => {
          if (generation !== this.photo.generation) return this.photo.promise;
          this.photo.blob = blob;
          this.showThumb(blob);
          return this.uploadPhoto();
        },
        () => {
          if (generation !== this.photo.generation) return this.photo.promise;
          this.photo.state = 'error';
          this.setPhotoStatus('We could not prepare that photo. Try another one.', 'error');
          throw new Error('photo_unreadable');
        }
      );
      this.photo.promise.catch(function () {});
      return this.photo.promise;
    }

    showThumb(blob) {
      if (!this.thumb || !this.thumbWrap) return;
      if (this.thumbUrl) URL.revokeObjectURL(this.thumbUrl);
      this.thumbUrl = URL.createObjectURL(blob);
      this.thumb.src = this.thumbUrl;
      this.thumbWrap.hidden = false;
    }

    uploadPhoto() {
      var generation = this.photo.generation;
      var blob = this.photo.blob;
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timeout = controller
        ? setTimeout(function () {
            controller.abort();
          }, 90000)
        : null;
      this.photo.controller = controller;
      this.photo.state = 'uploading';
      this.photo.photoId = '';
      this.resetVerdict();
      this.setPhotoStatus('Uploading and checking your photo…');

      var body = new FormData();
      body.append('file', blob, 'photo.jpg');
      body.append('category', this.category);

      var promise = fetch(this.origin + '/api/intake/photo', {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        body: body,
        signal: controller ? controller.signal : undefined,
      })
        .then((response) => readJson(response).then((json) => ({ response: response, json: json })))
        .then(
          (result) => {
            if (timeout) clearTimeout(timeout);
            if (generation !== this.photo.generation) return this.photo.promise;
            var json = result.json;
            if ((result.response.status === 201 || result.response.ok) && json && json.photoId) {
              this.photo.state = 'done';
              this.photo.photoId = String(json.photoId);
              this.setPhotoStatus('');
              this.showVerdict(json.line);
              this.scheduleSave();
              return this.photo.photoId;
            }
            var message = 'Your photo did not upload.';
            if (result.response.status === 429) message = 'Too many tries, wait a minute and retry.';
            else if (result.response.status === 400 || result.response.status === 413 || result.response.status === 415)
              message = 'We could not use that photo. Try a different JPG or PNG under 15 MB.';
            this.failUpload(message);
            throw new Error('photo_upload');
          },
          (error) => {
            if (timeout) clearTimeout(timeout);
            if (generation !== this.photo.generation) return this.photo.promise;
            this.failUpload('Your photo did not upload. Check your connection and retry.');
            throw error;
          }
        );
      this.photo.promise = promise;
      promise.catch(function () {});
      return promise;
    }

    failUpload(message) {
      this.photo.state = 'error';
      this.setPhotoStatus(message, 'error');
      if (this.retryButton) this.retryButton.hidden = false;
    }

    showVerdict(line) {
      if (!line || !this.badge) return;
      var tone = line.tone === 'green' || line.tone === 'amber' || line.tone === 'red' ? line.tone : 'amber';
      this.badge.textContent = line.badge || '';
      this.badge.className = 'tn-quick-form__badge is-' + tone;
      this.badge.hidden = !line.badge;
      if (this.detail) {
        this.detail.textContent = line.detail || '';
        this.detail.hidden = !line.detail;
      }
      if (this.redWarning) {
        this.redWarning.textContent = this.dataset.msgRed || '';
        this.redWarning.hidden = tone !== 'red' || !this.dataset.msgRed;
      }
    }

    /** Resolves to a photoId, retrying once if the last upload failed. */
    ensurePhoto() {
      /* Still in (or back from) the framing window: the framing on screen counts. */
      if (this.photo.state === 'cropping') {
        if (this.cropOpen) this.closeCrop(true);
        else this.confirmCrop();
        return this.photo.promise;
      }
      if (this.photo.state === 'done' && this.photo.photoId) return Promise.resolve(this.photo.photoId);
      if (this.photo.state === 'error' && this.photo.blob) return this.uploadPhoto();
      if (this.photo.promise) return this.photo.promise;
      return Promise.reject(new Error('no_photo'));
    }

    /* ----------------------------- GPX ----------------------------- */

    /**
     * The runner's own track (marathon page). Uploaded on pick, like the
     * photo, so "your track: 71.8 km" is on screen before the submit; the
     * Studio parses it there and hands back a signed id the submit sends.
     */
    setupGpx() {
      this.gpxInput = this.querySelector('[data-tnq-gpx]');
      this.gpxButton = this.querySelector('[data-tnq-gpx-button]');
      this.gpxStatus = this.querySelector('[data-tnq-gpx-status]');
      this.gpxClear = this.querySelector('[data-tnq-gpx-clear]');
      if (!this.gpxInput) return;
      this.gpxInput.addEventListener('change', () => {
        var file = this.gpxInput.files && this.gpxInput.files[0];
        if (file) this.uploadGpx(file);
      });
      if (this.gpxClear) this.gpxClear.addEventListener('click', () => this.clearGpx());
    }

    setGpxStatus(text, tone) {
      if (!this.gpxStatus) return;
      this.gpxStatus.textContent = text || '';
      this.gpxStatus.className = 'tn-quick-form__status' + (tone ? ' is-' + tone : '');
      if (this.gpxClear) this.gpxClear.hidden = !(this.gpx.state === 'done' || this.gpx.state === 'error');
    }

    gpxLine() {
      var km = Number(this.gpx.km);
      return 'Your track: ' + (km ? km.toFixed(1) + ' km' : this.gpx.name || 'added') + '.';
    }

    uploadGpx(file) {
      var generation = ++this.gpx.generation;
      this.clearError(this.gpxInput);
      this.gpx.gpxId = '';
      this.gpx.name = file.name || '';
      this.gpx.km = 0;
      if (file.size > MAX_GPX_BYTES) {
        this.gpx.state = 'error';
        this.setGpxStatus('That file is over 10 MB. Export the race on its own and try again.', 'error');
        return;
      }
      if (!/\.gpx$/i.test(file.name || '') && !/xml/i.test(file.type || '')) {
        this.gpx.state = 'error';
        this.setGpxStatus('That is not a GPX file. Export the race from your watch app as .gpx.', 'error');
        return;
      }
      this.gpx.state = 'uploading';
      this.setGpxStatus('Reading your track…');

      var body = new FormData();
      body.append('file', file, file.name || 'track.gpx');
      fetch(this.origin + '/api/intake/gpx', { method: 'POST', mode: 'cors', credentials: 'omit', body: body })
        .then((response) => readJson(response).then((json) => ({ response: response, json: json })))
        .then(
          (result) => {
            if (generation !== this.gpx.generation) return;
            var json = result.json;
            if ((result.response.status === 201 || result.response.ok) && json && json.gpxId) {
              this.gpx.state = 'done';
              this.gpx.gpxId = String(json.gpxId);
              this.gpx.km = Number(json.distanceKm) || 0;
              this.setGpxStatus(this.gpxLine(), 'green');
              this.scheduleSave();
              if (this.lead) this.fillOptions();
              return;
            }
            var message = 'We could not read that file.';
            if (result.response.status === 429) message = 'Too many tries, wait a minute and retry.';
            else if (result.response.status === 413) message = 'That file is over 10 MB.';
            else if (json && json.error) message = 'We could not read that file: ' + String(json.error) + '.';
            this.gpx.state = 'error';
            this.setGpxStatus(message, 'error');
          },
          () => {
            if (generation !== this.gpx.generation) return;
            this.gpx.state = 'error';
            this.setGpxStatus('Your file did not upload. Check your connection and try again.', 'error');
          }
        );
    }

    clearGpx() {
      this.gpx.generation++;
      this.gpx = { state: 'idle', gpxId: '', name: '', km: 0, generation: this.gpx.generation };
      if (this.gpxInput) this.gpxInput.value = '';
      this.setGpxStatus('');
      this.scheduleSave();
      if (this.lead) this.fillOptions();
    }

    /** True when the route comes from somewhere other than typed stops. */
    hasCourse() {
      return !!(this.presetId || (this.gpx.state === 'done' && this.gpx.gpxId));
    }

    isMarathon() {
      return this.category === 'marathon';
    }

    /* ---------------------------- stops ---------------------------- */

    setupStops() {
      if (this.addStopButton) {
        this.addStopButton.addEventListener('click', () => {
          var stop = this.addStop();
          if (stop) stop.input.focus();
        });
      }
    }

    addStop(place) {
      if (!this.stopTemplate || !this.stopsList || this.stops.length >= MAX_STOPS) return null;
      var fragment = this.stopTemplate.content.cloneNode(true);
      var row = fragment.querySelector('[data-tnq-stop]');
      var input = fragment.querySelector('[data-tnq-stop-input]');
      var remove = fragment.querySelector('[data-tnq-remove-stop]');
      this.stopsList.appendChild(fragment);
      var stop = { row: row, input: input, ac: new Autocomplete(this, input) };
      if (place) stop.ac.setPlace(place);
      this.stops.push(stop);
      remove.addEventListener('click', () => this.removeStop(stop));
      this.syncStops();
      return stop;
    }

    removeStop(stop) {
      if (this.stops.length <= 1) {
        stop.input.value = '';
        stop.ac.place = null;
      } else {
        this.stops.splice(this.stops.indexOf(stop), 1);
        stop.row.remove();
      }
      this.syncStops();
      this.scheduleSave();
    }

    /** The first stop is where the journey began: the Studio makes it the entry point. */
    syncStops() {
      this.stops.forEach((stop, i) => {
        if (!stop.placeholder) stop.placeholder = stop.input.getAttribute('placeholder') || '';
        stop.input.setAttribute('aria-label', i === 0 ? 'Where your journey began' : 'Stop ' + (i + 1));
        stop.input.setAttribute('placeholder', i === 0 ? FIRST_STOP_PLACEHOLDER : stop.placeholder);
      });
      if (this.addStopButton) this.addStopButton.hidden = this.stops.length >= MAX_STOPS;
    }

    /* ---------------------------- rating --------------------------- */

    setupRating() {
      this.ratingInput = this.input('trip_rating');
      this.stars = Array.prototype.slice.call(this.querySelectorAll('[data-tnq-star]'));
      this.stars.forEach((star) => {
        star.addEventListener('click', () => {
          if (!this.ratingInput) return;
          var n = star.getAttribute('data-tnq-star');
          /* Tapping the current rating again clears it: the question is optional. */
          this.ratingInput.value = this.ratingInput.value === n ? '' : n;
          this.paintRating();
          this.scheduleSave();
        });
      });
    }

    paintRating() {
      var value = Number((this.ratingInput && this.ratingInput.value) || 0);
      (this.stars || []).forEach(function (star) {
        var n = Number(star.getAttribute('data-tnq-star'));
        star.classList.toggle('is-on', n <= value);
        star.setAttribute('aria-pressed', n === value ? 'true' : 'false');
      });
    }

    /* --------------------------- whatsapp -------------------------- */

    setupWhatsApp() {
      var input = this.input('whatsapp');
      if (!input) return;
      input.addEventListener('input', () => {
        var hasPlus = input.value.trim().charAt(0) === '+';
        var digits = input.value.replace(/\D/g, '');
        var clean = (hasPlus ? '+' : '') + digits;
        if (clean !== input.value) input.value = clean;
      });
    }

    /* -------------------------- validation ------------------------- */

    validate() {
      var errors = [];
      var firstChoice = (key) => this.querySelector('[data-tnq-choice="' + key + '"]');
      if (this.asked('who') && firstChoice('who') && !this.choice('who')) {
        errors.push({ el: firstChoice('who'), msg: this.copy.whoError || 'Please pick who it is for.' });
      }
      if (this.asked('when') && firstChoice('when') && !this.choice('when')) {
        errors.push({ el: firstChoice('when'), msg: this.copy.whenError || 'Please pick when you need it.' });
      } else if (this.needsDate() && !this.value('needed_by')) {
        errors.push({ el: this.input('needed_by'), msg: this.copy.neededByError || 'Please pick the date.' });
      }
      if (this.photo.state === 'idle' || (this.photo.state === 'error' && !this.photo.blob)) {
        errors.push({ el: this.photoInput, msg: 'Please add your photo.' });
      }
      if (!this.value('title')) errors.push({ el: this.input('title'), msg: 'Please enter the poster title.' });

      if (this.gpx.state === 'uploading') {
        errors.push({ el: this.gpxInput, msg: 'Your GPX file is still uploading. One moment.' });
      }
      var hasStop = this.stops.some(function (stop) {
        return stop.input.value.trim();
      });
      if (!hasStop && !this.hasCourse()) {
        if (this.stops[0]) errors.push({ el: this.stops[0].input, msg: this.isMarathon() ? 'Please add the start line, or your GPX file.' : 'Please add where your journey began.' });
        else if (this.gpxInput) errors.push({ el: this.gpxInput, msg: 'Please add your GPX file.' });
      }

      var start = this.value('start_date');
      var end = this.value('end_date');
      if (!start) errors.push({ el: this.input('start_date'), msg: this.isMarathon() ? 'Please pick your race day.' : 'Please pick your start date.' });
      if (!this.isMarathon()) {
        if (!end) errors.push({ el: this.input('end_date'), msg: 'Please pick your end date.' });
        if (start && end && end < start) errors.push({ el: this.input('end_date'), msg: 'End date is before the start date.' });
      }

      var whatsapp = this.value('whatsapp');
      if (!whatsapp) errors.push({ el: this.input('whatsapp'), msg: 'Please enter your WhatsApp number.' });
      else if (!isValidPhone(whatsapp))
        errors.push({ el: this.input('whatsapp'), msg: 'Enter a 10-digit number, or start with your + country code.' });

      ['distance_km', 'days', 'nights'].forEach(function (key) {
        var number = this.value(key);
        if (number && !isFinite(Number(number))) errors.push({ el: this.input(key), msg: 'Please enter a number.' });
      }, this);
      return errors;
    }

    fieldContainer(el) {
      if (!el) return null;
      return el.closest('[data-tnq-stop]') || el.closest('[data-tnq-field]');
    }

    clearError(el) {
      var container = this.fieldContainer(el);
      if (!container) return;
      var p = container.querySelector(':scope > .tn-quick-form__error');
      if (p) p.remove();
      if (el.removeAttribute) el.removeAttribute('aria-invalid');
    }

    clearErrors() {
      this.querySelectorAll('.tn-quick-form__error').forEach(function (p) {
        p.remove();
      });
      this.querySelectorAll('[aria-invalid="true"]').forEach(function (el) {
        el.removeAttribute('aria-invalid');
      });
      this.hideBanner();
    }

    showErrors(errors) {
      this.clearErrors();
      var first = null;
      errors.forEach((error) => {
        var el = error.el;
        var container = this.fieldContainer(el);
        if (!el || !container) return;
        if (!first) first = el;
        el.setAttribute('aria-invalid', 'true');
        if (!container.querySelector(':scope > .tn-quick-form__error')) {
          var p = document.createElement('p');
          p.className = 'tn-quick-form__error';
          p.textContent = error.msg;
          container.appendChild(p);
        }
      });
      if (first) {
        if (first.closest('details') && !first.closest('details').open) first.closest('details').open = true;
        var target = first.type === 'file' ? this.fieldContainer(first) : first;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (first.type !== 'file') {
          setTimeout(function () {
            first.focus({ preventScroll: true });
          }, 350);
        }
      }
    }

    /** Maps the Studio's 400 issues ({path: "fields.title" | ["stops", 0, "name"], message}) inline. */
    showServerIssues(issues) {
      var errors = [];
      (issues || []).forEach((issue) => {
        var path = Array.isArray(issue.path) ? issue.path.map(String) : String(issue.path || '').split('.');
        var head = path[0];
        var el = null;
        if (head === 'fields') el = this.input(path[1]);
        else if (head === 'entry') el = this.stops[0] ? this.stops[0].input : null;
        else if (head === 'stops') {
          var index = Number(path[1]);
          var stop = isFinite(index) && this.stops[index] ? this.stops[index] : this.stops[0];
          el = stop ? stop.input : null;
        } else if (head === 'whatsapp') el = this.input('whatsapp');
        else if (head === 'notes') el = this.input('notes');
        else if (head === 'photoId') el = this.photoInput;
        else if (head === 'gpxId') el = this.gpxInput;
        if (!el) return;
        var message = ISSUE_TEXT[issue.message] || 'Please check this.';
        if (head === 'photoId') message = 'Please add your photo again.';
        if (head === 'gpxId') {
          this.gpx.state = 'error';
          this.gpx.gpxId = '';
          this.setGpxStatus('', 'error');
        }
        errors.push({ el: el, msg: message });
      });
      if (errors.length) this.showErrors(errors);
      else this.showBanner('Some details need a second look. Please check the form and try again.');
    }

    showBanner(text, withWhatsApp) {
      if (!this.banner) return;
      this.banner.textContent = text;
      if (withWhatsApp) {
        this.banner.appendChild(document.createTextNode(' '));
        var a = document.createElement('a');
        a.href = WHATSAPP_HELP;
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = this.copy.helpLink || 'Message us on WhatsApp';
        this.banner.appendChild(a);
        this.banner.appendChild(document.createTextNode('.'));
      }
      this.banner.hidden = false;
      this.banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /**
     * Framed was chosen but that frame cannot be bought. With another frame in
     * stock, point at the picker; with none, say so and offer Priority design
     * in one tap (everything typed stays).
     */
    framedUnavailable(chosenSoldOut) {
      var rp2 = this.rp2();
      if (rp2 && rp2.framesAvailable()) {
        this.showBanner(
          chosenSoldOut
            ? this.copy.frameSoldOut || 'That frame just sold out. Please choose another frame above.'
            : this.copy.chooseFrame || 'Please choose a frame above.'
        );
        rp2.scrollTo('frames');
        return;
      }
      this.showBanner(
        this.copy.framesSoldOut ||
          'Frames are sold out right now. Partial pay gets you the same hand-made design, and nothing you typed is lost.'
      );
      if (!this.banner) return;
      this.banner.appendChild(document.createTextNode(' '));
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'tn-quick-form__link';
      button.textContent = this.copy.switchPriority || 'Switch to partial pay';
      button.addEventListener('click', () => {
        this.hideBanner();
        this.switchToPriority();
      });
      this.banner.appendChild(button);
    }

    hideBanner() {
      if (this.banner) this.banner.hidden = true;
    }

    setProgress(text) {
      if (!this.progress) return;
      this.progress.textContent = text || '';
      this.progress.hidden = !text;
    }

    /** One submit at a time: the flag stops a second tap, the disabled button shows it. */
    setBusy(busy) {
      this.submitting = busy;
      if (busy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      if (!this.submitButton) return;
      this.submitButton.disabled = busy;
      this.submitButton.textContent = busy ? this.copy.submitBusy || this.submitLabel : this.submitLabel;
    }

    /* ---------------------------- submit --------------------------- */

    /** The variant the chosen package maps to (tn-rp2.js), never the Free preview one. */
    variantId() {
      var rp2 = this.rp2();
      if (!rp2 || !rp2.state.variantId) return null;
      var id = String(rp2.state.variantId);
      if (rp2.isFreeVariant(id)) return null;
      return id;
    }

    payload(photoId, pkg) {
      var fields = { title: this.value('title'), start_date: this.value('start_date') };
      /* A race has one date; the Studio refuses an end date it did not ask for. */
      if (!this.isMarathon()) fields.end_date = this.value('end_date');
      OPTIONAL_FIELDS[this.category].forEach((key) => {
        var v = this.value(key);
        if (v) fields[key] = v;
      });

      var source = trackingSource();
      /* Lets the Studio (and anyone reading the intake) see which package it came from. */
      try {
        var page = new URL(source.page || window.location.href);
        page.searchParams.set('tn_package', pkg);
        source.page = page.href.slice(0, 500);
      } catch (e) {
        /* the page link is only a hint */
      }

      var body = {
        category: this.category,
        fields: fields,
        stops: this.stops
          .map(function (stop) {
            return stop.ac.value();
          })
          .filter(Boolean)
          .slice(0, MAX_STOPS),
        photoId: photoId,
        whatsapp: this.value('whatsapp'),
        source: source,
      };
      /* The course, when the form collected no stops: the product's, or their own track (which the Studio prefers). */
      if (this.presetId) body.presetId = this.presetId;
      if (this.gpx.state === 'done' && this.gpx.gpxId) body.gpxId = this.gpx.gpxId;

      /*
       * The package and the two tap choices are the Studio's own fields
       * (Intake.package / forWhom / whenNeeded): free previews get their own
       * queue in /admin/intake, and a paid package starts the draft-date
       * clock there. Only the upgrade trail still rides in the note.
       */
      body.package = PACKAGE_IDS[pkg] || 'priority';
      if (this.choice('who')) body.forWhom = this.choice('who').slice(0, 60);
      if (this.whenText()) body.whenNeeded = this.whenText().slice(0, 80);
      var notes = this.value('notes');
      if (pkg !== 'free' && this.freeIntake && this.freeIntake.ref) {
        notes = 'Upgraded from free preview ' + this.freeIntake.ref + (notes ? '\n\n' + notes : '');
      }
      if (notes) body.notes = notes.slice(0, 1000);
      var website = this.honeypot ? this.honeypot.value : '';
      body.website = website.slice(0, 200);
      return body;
    }

    createIntake(body) {
      /* Same trip sent twice (cart failed, customer retried): reuse the intake. */
      var signature = JSON.stringify(Object.assign({}, body, { source: null }));
      if (this.lastIntake && this.lastIntake.signature === signature) return Promise.resolve(this.lastIntake);

      return fetch(this.origin + '/api/intake', {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((response) =>
        readJson(response).then((json) => {
          if ((response.status === 201 || response.ok) && json && json.intakeId) {
            this.lastIntake = { signature: signature, intakeId: String(json.intakeId), ref: String(json.ref || '') };
            return this.lastIntake;
          }
          var error = new Error('intake');
          error.status = response.status;
          error.json = json;
          throw error;
        })
      );
    }

    addToCart(variantId, intake, title) {
      var sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map(function (el) {
          return el.dataset.sectionId;
        })
        .filter(Boolean);
      var properties = {
        _studio_intake_id: intake.intakeId,
        _studio_intake_ref: intake.ref,
        Poster: title,
      };
      if (intake.ref) properties.Ref = intake.ref;
      if (this.choice('who')) properties['Who is it for?'] = this.choice('who');
      if (this.whenText()) properties['When do you need it?'] = this.whenText();
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionIds.length ? sectionIds.join(',') : undefined,
          sections_url: window.location.pathname,
          properties: properties,
        }),
      }).then(function (response) {
        if (!response.ok) throw new Error('cart');
        return readJson(response);
      });
    }

    announceCart(cartJson, variantId) {
      try {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cartJson,
              sourceId: 'tn-rp2-form',
              data: {
                source: 'product-form-component',
                itemCount: 1,
                variantId: String(variantId),
                sections: (cartJson && cartJson.sections) || undefined,
              },
            },
          })
        );
      } catch (e) {
        /* the drawer still opens below */
      }
    }

    openCart() {
      var drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = '/cart';
    }

    /**
     * Free preview accepted by the Studio: tell the ad pixels it was a Lead,
     * once per intake, and never a Purchase (nothing goes near the cart).
     */
    fireLead(intake) {
      if (!intake || this.leadSent[intake.intakeId]) return;
      this.leadSent[intake.intakeId] = true;
      var params = {
        content_name: this.dataset.productTitle || document.title,
        content_category: this.copy.leadCategory || 'free_preview',
        value: 0,
        currency: 'INR',
      };
      /*
       * TODO: send the same Lead server-side through the Meta Conversions API
       * (from the Studio, when it stores an intake with package
       * "free_preview"), with event_id "lead_<intakeId>" so Meta
       * de-duplicates it against this browser event.
       */
      try {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', params, { eventID: 'lead_' + intake.intakeId });
        }
      } catch (e) {
        /* tracking is never worth a broken confirmation */
      }
      try {
        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push(Object.assign({ event: 'free_preview_submitted', intake_ref: intake.ref }, params));
        }
      } catch (e) {
        /* as above */
      }
      /*
       * Shopify's Meta app runs its pixel in a sandbox where window.fbq does
       * not exist. A custom pixel (Settings > Customer events) can subscribe
       * to this event and forward it; see docs/route-poster-v2.md.
       */
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('free_preview_submitted', Object.assign({ intake_id: intake.intakeId, intake_ref: intake.ref }, params));
        }
      } catch (e) {
        /* as above */
      }
    }

    showConfirmation() {
      if (this.body) this.body.hidden = true;
      if (!this.confirm) return;
      this.confirm.hidden = false;
      this.confirm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => this.confirm.focus({ preventScroll: true }), 350);
    }

    async submit() {
      if (this.submitting) return;
      if (this.flow === 'options') return this.submitLead();
      var pkg = this.pkg();
      var rp2 = this.rp2();

      /* Free preview for an older trip: point at the note, do not scold. */
      if (pkg === 'free' && this.freeTooOld()) {
        this.syncFreeNote();
        if (this.freeNote) {
          this.freeNote.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var noteButton = this.freeNote.querySelector('button');
          if (noteButton) setTimeout(() => noteButton.focus({ preventScroll: true }), 350);
        }
        return;
      }

      var errors = this.validate();
      if (errors.length) {
        this.showErrors(errors);
        return;
      }
      this.clearErrors();

      var variantId = null;
      if (pkg !== 'free') {
        variantId = this.variantId();
        var variant = rp2 && variantId ? rp2.variant(variantId) : null;
        if (pkg === 'framed' && !(variant && variant.available)) {
          this.framedUnavailable(!!variant);
          return;
        }
        if (!variantId || !variant || !variant.available) {
          this.showBanner(this.copy.unavailable || 'This option is not available right now.', true);
          return;
        }
      }

      this.setBusy(true);
      try {
        /* 1. The photo, usually uploaded long ago. */
        this.setProgress('Finishing your photo upload…');
        var photoId;
        try {
          photoId = await this.ensurePhoto();
        } catch (e) {
          this.setProgress('');
          this.showErrors([
            { el: this.photoInput, msg: 'Your photo has not uploaded yet. Tap "Retry upload", or pick it again.' },
          ]);
          return;
        }

        /* 2. The trip, to the Studio (the "Saving your trip" step). */
        this.setProgress(pkg === 'free' ? this.copy.sending || 'Sending your trip…' : 'Saving your trip…');
        var body = this.payload(photoId, pkg);
        var intake;
        try {
          intake = await this.createIntake(body).catch((error) => {
            var legacy = legacyBody(body, error);
            if (!legacy) throw error;
            return this.createIntake(legacy);
          });
        } catch (error) {
          this.setProgress('');
          if (error && error.status === 400 && error.json && error.json.error === 'invalid') {
            this.showServerIssues(error.json.issues);
          } else if (error && error.status === 429) {
            this.showBanner('Too many tries, wait a minute and try again.');
          } else {
            this.showBanner(
              pkg === 'free'
                ? this.copy.freeError || 'We could not send your trip just now. Please try again in a moment.'
                : this.copy.saveError || 'We could not save your trip just now. Please try again in a moment.',
              true
            );
          }
          return;
        }

        /* Free preview ends here: no cart, no checkout. */
        if (pkg === 'free') {
          this.setProgress('');
          this.freeIntake = intake;
          this.fireLead(intake);
          this.save(); /* kept, so "Upgrade to priority" needs no retyping */
          this.showConfirmation();
          return;
        }

        /* 3. The cart. */
        this.setProgress('Adding to your cart…');
        var cartJson;
        try {
          cartJson = await this.addToCart(variantId, intake, body.fields.title);
        } catch (e) {
          this.setProgress('');
          this.showBanner(this.copy.cartError || 'Your trip is saved, but it did not reach the cart. Please try again.', true);
          return;
        }

        this.setProgress('');
        this.lastIntake = null; /* another add is another poster */
        this.freeIntake = null;
        this.clearState();
        this.announceCart(cartJson, variantId);
        this.openCart();
      } finally {
        this.setBusy(false);
      }
    }

    /* ------------------- form first, options after ------------------- */

    /** Funnel steps for GTM and Shopify custom pixels. Each fires once per page. */
    track(name, params) {
      if (this.flow !== 'options' || this.tracked[name]) return;
      this.tracked[name] = true;
      var detail = Object.assign({ product_title: this.dataset.productTitle || '' }, params || {});
      try {
        if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: name }, detail));
      } catch (e) {
        /* tracking never breaks the form */
      }
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish(name, detail);
        }
      } catch (e) {
        /* as above */
      }
    }

    /**
     * Step 1: the photo and the trip go to the Studio with no package. That
     * row is the lead, whatever they pick next (or if they pick nothing).
     */
    async submitLead() {
      var errors = this.validate();
      if (errors.length) {
        this.showErrors(errors);
        return;
      }
      this.clearErrors();
      this.hideBanner();

      this.setBusy(true);
      try {
        this.setProgress(this.copy.creating || 'Creating your poster…');
        var photoId;
        try {
          photoId = await this.ensurePhoto();
        } catch (e) {
          this.setProgress('');
          this.showErrors([
            { el: this.photoInput, msg: 'Your photo has not uploaded yet. Tap "Retry upload", or pick it again.' },
          ]);
          return;
        }

        var body = this.payload(photoId, 'priority');
        delete body.package;
        try {
          var page = new URL(body.source.page);
          page.searchParams.set('tn_package', 'lead');
          body.source.page = page.href.slice(0, 500);
        } catch (e) {
          /* the page link is only a hint */
        }

        var intake;
        try {
          intake = await this.createIntake(body).catch((error) => {
            var legacy = legacyBody(body, error);
            if (!legacy) throw error;
            return this.createIntake(legacy);
          });
        } catch (error) {
          this.setProgress('');
          if (error && error.status === 400 && error.json && error.json.error === 'invalid') {
            this.showServerIssues(error.json.issues);
          } else if (error && error.status === 429) {
            this.showBanner('Too many tries, wait a minute and try again.');
          } else {
            this.showBanner(
              this.copy.saveError || 'We could not save your trip just now. Please try again in a moment.',
              true
            );
          }
          return;
        }

        this.setProgress('');
        this.lead = intake;
        this.added = null;
        this.fireLead(intake);
        this.track('rp2_poster_created', { intake_ref: intake.ref });
        this.save();
        this.showOptions();
      } finally {
        this.setBusy(false);
      }
    }

    setupOptions() {
      this.options = this.querySelector('[data-tn-rp2-options]');
      this.resume = this.querySelector('[data-tn-rp2-resume]');
      if (!this.options) return;
      /* The sheet lives in the top layer: out of the section, so no ancestor clips or stacks it. */
      document.body.appendChild(this.options);
      this.optionsBanner = this.options.querySelector('[data-tn-rp2-options-banner]');
      this.continueButton = this.options.querySelector('[data-tn-rp2-options-continue]');
      this.frameControls = this.options.querySelector('[data-tn-rp2-frame-controls]');
      this.picked = this.options.dataset.default === 'framed' ? 'framed' : 'priority';
      this.frameType = null;
      this.frameSize = null;

      this.options.addEventListener('change', (event) => {
        var input = event.target;
        if (input && input.matches('[data-tn-rp2-pick-input]')) {
          this.picked = input.value;
          this.hideOptionsBanner();
          this.fillOptions();
        }
      });
      this.options.addEventListener('click', (event) => {
        var target = event.target;
        if (!target || !target.closest) return;
        var seg = target.closest('[data-seg-value]');
        if (seg && !seg.disabled) {
          var group = seg.closest('[data-tn-rp2-seg]').getAttribute('data-tn-rp2-seg');
          if (group === 'type') this.frameType = seg.getAttribute('data-seg-value');
          else this.frameSize = seg.getAttribute('data-seg-value');
          this.picked = 'framed';
          this.fillOptions();
          return;
        }
        if (target.closest('[data-tn-rp2-options-close]')) return this.closeOptions();
        if (target.closest('[data-tn-rp2-options-edit]')) return this.editTrip();
        if (target.closest('[data-tn-rp2-options-continue]')) return this.choose(this.picked);
        if (target.closest('[data-tn-rp2-options-choose="free"]')) return this.choose('free');
        /* A tap on the dimmed backdrop (the dialog itself, outside the panel) closes it. */
        if (target === this.options) this.closeOptions();
      });
      this.options.addEventListener('close', () => this.onOptionsClosed());

      if (this.resume) {
        this.resume.addEventListener('click', (event) => {
          var target = event.target;
          if (!target || !target.closest) return;
          if (target.closest('[data-tn-rp2-options-open]')) this.showOptions();
          else if (target.closest('[data-tn-rp2-options-edit]')) this.editTrip();
        });
      }
    }

    /** The frames the page sells (RP2 frames section), with their variants. */
    frameOptions() {
      var rp2 = this.rp2();
      if (!rp2) return [];
      return (rp2.config.frames || [])
        .map(function (frame) {
          var v = rp2.variant(frame.variantId);
          return v
            ? {
                id: String(frame.variantId),
                type: frame.type || 'metal',
                title: frame.title || '',
                size: frame.size || '',
                description: frame.description || '',
                careText: frame.careText || '',
                image: frame.image || '',
                imageAlt: frame.imageAlt || '',
                variant: v,
              }
            : null;
        })
        .filter(Boolean);
    }

    /** The frame for the chosen type and size, falling back to one in stock. */
    chosenFrame() {
      var frames = this.frameOptions().filter(function (f) {
        return f.variant.available;
      });
      if (!frames.length) return null;
      var type = this.frameType;
      if (!frames.some((f) => f.type === type)) type = frames[0].type;
      var ofType = frames.filter((f) => f.type === type);
      var pick = ofType.find((f) => f.size === this.frameSize) || ofType[0];
      this.frameType = pick.type;
      this.frameSize = pick.size;
      return pick;
    }

    /** Dates as the poster shows them: "8 – 16 Nov 2025". */
    posterDates() {
      var parse = function (iso) {
        var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
        return m ? { y: m[1], m: Number(m[2]), d: Number(m[3]) } : null;
      };
      var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      var a = parse(this.value('start_date'));
      var b = parse(this.value('end_date'));
      if (!a) return '';
      if (!b) return a.d + ' ' + MONTHS[a.m - 1] + ' ' + a.y;
      if (a.y === b.y && a.m === b.m) return a.d + ' – ' + b.d + ' ' + MONTHS[b.m - 1] + ' ' + b.y;
      if (a.y === b.y) return a.d + ' ' + MONTHS[a.m - 1] + ' – ' + b.d + ' ' + MONTHS[b.m - 1] + ' ' + b.y;
      return a.d + ' ' + MONTHS[a.m - 1] + ' ' + a.y + ' – ' + b.d + ' ' + MONTHS[b.m - 1] + ' ' + b.y;
    }

    /** One segmented control: a button per value, the chosen one pressed, sold-out ones disabled, an optional tag. */
    paintSeg(group, values, current, isAvailable, labelFor, tagFor) {
      var box = this.options.querySelector('[data-tn-rp2-seg="' + group + '"]');
      if (!box) return;
      box.textContent = '';
      values.forEach(function (value) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'tn-rp2-seg__option';
        button.setAttribute('role', 'radio');
        button.setAttribute('data-seg-value', value);
        button.setAttribute('aria-checked', value === current ? 'true' : 'false');
        button.disabled = !isAvailable(value);
        button.textContent = labelFor(value);
        var tag = tagFor ? tagFor(value) : '';
        if (tag) {
          var pill = document.createElement('span');
          pill.className = 'tn-rp2-seg__tag';
          pill.textContent = tag;
          button.appendChild(pill);
        }
        box.appendChild(button);
      });
    }

    /** The chosen frame's photo and copy, and the Metal vs Classic comparison, under the Frame switch. */
    fillFrameLook(rp2, frames, chosen) {
      var look = this.options.querySelector('[data-tn-rp2-frame-look]');
      if (look) {
        var type = chosen ? chosen.type : frames.length ? frames[0].type : '';
        var frame = frames.find((f) => f.type === type && (f.image || f.description)) || null;
        var img = look.querySelector('img');
        var text = look.querySelector('figcaption');
        var copy = frame ? [frame.description, frame.careText].filter(Boolean).join(' ') : '';
        if (img) {
          if (frame && frame.image) {
            if (img.getAttribute('src') !== frame.image) img.src = frame.image;
            img.alt = frame.imageAlt || frame.title;
            img.hidden = false;
          } else {
            img.hidden = true;
          }
        }
        if (text) {
          text.textContent = copy;
          text.hidden = !copy;
        }
        look.hidden = !frame;
      }

      var compare = this.options.querySelector('[data-tn-rp2-frame-compare]');
      var data = rp2 ? rp2.config.frameCompare : null;
      if (!compare || compare.dataset.filled) return;
      if (!data || !data.rows) return;
      var summary = compare.querySelector('summary');
      var table = compare.querySelector('table');
      if (summary) summary.textContent = data.title || '';
      if (table) {
        var head = table.createTHead().insertRow();
        head.appendChild(document.createElement('td'));
        (data.columns || []).forEach(function (name) {
          var th = document.createElement('th');
          th.scope = 'col';
          th.textContent = name;
          head.appendChild(th);
        });
        var body = table.createTBody();
        data.rows.forEach(function (row) {
          if (!row[0]) return;
          var tr = body.insertRow();
          var th = document.createElement('th');
          th.scope = 'row';
          th.textContent = row[0];
          tr.appendChild(th);
          row.slice(1).forEach(function (cell) {
            tr.insertCell().textContent = cell || '';
          });
        });
      }
      compare.dataset.filled = '1';
      compare.hidden = false;
    }

    /** Everything on the sheet and the resume card, from what they typed, the pick and the stock. */
    fillOptions() {
      var rp2 = this.rp2();
      var roots = [this.options, this.resume].filter(Boolean);

      /* Thumbnail: their crop under the poster's layout overlay. */
      if (this.photo.blob && (!this.posterUrl || this.posterBlob !== this.photo.blob)) {
        if (this.posterUrl) URL.revokeObjectURL(this.posterUrl);
        this.posterUrl = URL.createObjectURL(this.photo.blob);
        this.posterBlob = this.photo.blob;
      }
      var title = this.value('title');
      var places = this.stops
        .map(function (stop) {
          var place = stop.ac.value();
          return place && place.name ? place.name : '';
        })
        .filter(Boolean);
      var route = places.length > 1 ? places[0] + ' → ' + places[places.length - 1] : places[0] || '';
      if (!route && this.gpx.state === 'done' && this.gpx.gpxId) route = this.gpxLine().replace(/\.$/, '');
      else if (!route && this.presetId) route = this.courseName ? this.courseName + ' course' : 'The course';
      var meta = [this.posterDates(), route].filter(Boolean).join(' · ');
      roots.forEach((root) => {
        root.querySelectorAll('[data-tn-rp2-poster-img]').forEach((img) => {
          if (!this.posterUrl) return;
          img.src = this.posterUrl;
          img.hidden = false;
        });
        root.querySelectorAll('[data-tn-rp2-poster-title]').forEach(function (el) {
          el.textContent = title;
        });
        root.querySelectorAll('[data-tn-rp2-poster-meta]').forEach(function (el) {
          el.textContent = meta;
        });
      });
      if (!this.options) return;

      /* What can be picked right now. */
      var priority = rp2 ? rp2.variant(rp2.config.packages.priority) : null;
      var canPriority = !!(priority && priority.available);
      var frames = this.frameOptions();
      var chosen = this.chosenFrame();
      var canFramed = !!chosen;
      if (this.picked === 'framed' && !canFramed) this.picked = 'priority';
      if (this.picked === 'priority' && !canPriority && canFramed) this.picked = 'framed';

      this.options.querySelectorAll('[data-tn-rp2-pick]').forEach((card) => {
        var kind = card.getAttribute('data-tn-rp2-pick');
        var can = kind === 'framed' ? canFramed : canPriority;
        /* Framed stays on show when every frame is sold out: greyed, not pickable. */
        card.hidden = kind === 'framed' ? !frames.length : !can;
        card.classList.toggle('is-sold-out', !can);
        card.classList.toggle('is-selected', can && kind === this.picked);
        var input = card.querySelector('[data-tn-rp2-pick-input]');
        if (input) {
          input.disabled = !can;
          input.checked = can && kind === this.picked;
        }
      });

      /* Framed: the chosen frame's price when picked, "from" the lowest otherwise. */
      var lowest = null;
      frames.forEach(function (f) {
        if (f.variant.available && (!lowest || f.variant.price < lowest.price)) lowest = f.variant;
      });
      var framedPrice = this.options.querySelector('[data-tn-rp2-pick-price="framed"]');
      if (framedPrice) {
        framedPrice.textContent = !canFramed
          ? this.copy.soldOut || 'Sold out'
          : this.picked === 'framed' && chosen
            ? chosen.variant.priceText
            : 'from ' + lowest.priceText;
      }
      var priorityPrice = this.options.querySelector('[data-tn-rp2-pick-price="priority"]');
      var priorityText = priority ? priority.priceText : '';
      if (priorityPrice) priorityPrice.textContent = priorityText;
      this.options.querySelectorAll('[data-tn-rp2-options-fill]').forEach(function (el) {
        el.textContent = el.getAttribute('data-tn-rp2-options-fill').split('[price]').join(priorityText);
      });

      var recommended = (rp2 && rp2.config.frameRecommended) || {};
      var recommendedTag = function (type) {
        return recommended.type && recommended.type === type ? recommended.label || '' : '';
      };

      /* Frame and size switches: while Framed is picked, or greyed out when every frame is sold out. */
      if (this.frameControls) this.frameControls.hidden = !((this.picked === 'framed' && chosen) || (!canFramed && frames.length));
      var soldOutNote = this.options.querySelector('[data-tn-rp2-framed-soldout]');
      if (soldOutNote) soldOutNote.hidden = canFramed || !canPriority;
      if (!canFramed && frames.length) {
        var allTypes = [];
        var allTitles = {};
        frames.forEach(function (f) {
          if (allTypes.indexOf(f.type) === -1) allTypes.push(f.type);
          if (!allTitles[f.type]) allTitles[f.type] = f.title.replace(/\s*frame\s*$/i, '') || f.type;
        });
        var allSizes = [];
        frames.forEach(function (f) {
          if (allSizes.indexOf(f.size) === -1) allSizes.push(f.size);
        });
        var none = function () {
          return false;
        };
        this.paintSeg('type', allTypes, null, none, (t) => allTitles[t], recommendedTag);
        this.paintSeg('size', allSizes, null, none, (z) => z);
      }
      if (chosen) {
        var inStock = frames.filter(function (f) {
          return f.variant.available;
        });
        var types = [];
        var titles = {};
        frames.forEach(function (f) {
          if (types.indexOf(f.type) === -1) types.push(f.type);
          if (!titles[f.type]) titles[f.type] = f.title.replace(/\s*frame\s*$/i, '') || f.type;
        });
        var sizes = [];
        frames
          .filter((f) => f.type === this.frameType)
          .forEach(function (f) {
            if (sizes.indexOf(f.size) === -1) sizes.push(f.size);
          });
        this.paintSeg(
          'type',
          types,
          this.frameType,
          (t) => inStock.some((f) => f.type === t),
          (t) => titles[t],
          recommendedTag
        );
        this.paintSeg(
          'size',
          sizes,
          this.frameSize,
          (z) => inStock.some((f) => f.type === this.frameType && f.size === z),
          (z) => z
        );
      }

      this.fillFrameLook(rp2, frames, chosen);

      /* The thumbnails wear the chosen frame. */
      var frameLook = this.picked === 'framed' && chosen ? chosen.type : 'none';
      this.options.querySelectorAll('[data-tn-rp2-poster]').forEach(function (el) {
        el.setAttribute('data-frame', frameLook);
      });

      /* The one button, with the total. */
      if (this.continueButton && !this.choosing) {
        var price = this.picked === 'framed' ? (chosen ? chosen.variant.priceText : '') : priorityText;
        this.continueButton.textContent = (this.continueButton.dataset.label || '')
          .replace('[price]', price)
          .replace(/\s*·\s*$/, '')
          .trim();
        this.continueButton.disabled = !(canFramed || canPriority);
      }

      /* Free preview only for recent trips. */
      var free = this.options.querySelector('[data-tn-rp2-options-free]');
      if (free) free.hidden = this.freeTooOld();
    }

    showOptions() {
      if (!this.options) return;
      this.fillOptions();
      if (this.header) this.header.hidden = true;
      if (this.body) this.body.hidden = true;
      if (this.confirm) this.confirm.hidden = true;
      if (this.resume) this.resume.hidden = false;
      this.hideOptionsBanner();
      if (!this.options.open) {
        if (typeof this.options.showModal === 'function') this.options.showModal();
        else this.options.setAttribute('open', '');
      }
      document.documentElement.classList.add('tn-rp2-sheet-open');
      var section = this.closest('.tn-rp2-form-section') || this;
      section.scrollIntoView({ block: 'start' });
    }

    closeOptions() {
      if (!this.options) return;
      if (this.options.open && typeof this.options.close === 'function') this.options.close();
      else {
        this.options.removeAttribute('open');
        this.onOptionsClosed();
      }
    }

    onOptionsClosed() {
      document.documentElement.classList.remove('tn-rp2-sheet-open');
    }

    /** Back to the form, everything as typed. Submitting again saves a fresh lead. */
    editTrip() {
      this.closeOptions();
      if (this.resume) this.resume.hidden = true;
      if (this.header) this.header.hidden = false;
      if (this.body) this.body.hidden = false;
      this.lead = null;
      this.lastIntake = null;
      var section = this.closest('.tn-rp2-form-section') || this;
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    showOptionsBanner(text) {
      if (!this.optionsBanner) return;
      this.optionsBanner.textContent = text;
      this.optionsBanner.hidden = false;
    }

    hideOptionsBanner() {
      if (this.optionsBanner) this.optionsBanner.hidden = true;
    }

    /**
     * Names the package on the lead (POST /api/intake/package). Best effort:
     * the cart line carries the intake either way, so a Studio that cannot
     * answer never stops a sale; staff then see the row without a package.
     */
    namePackage(pkg) {
      if (!this.lead) return Promise.resolve();
      return fetch(this.origin + '/api/intake/package', {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ intakeId: this.lead.intakeId, ref: this.lead.ref, package: PACKAGE_IDS[pkg] || pkg }),
      })
        .then(function (response) {
          return response.ok;
        })
        .catch(function () {
          return false;
        });
    }

    /** Free preview ends in the confirmation; a paid pick goes to checkout (or the cart). */
    async choose(pkg) {
      if (this.choosing || !this.lead) return;
      var rp2 = this.rp2();
      this.hideOptionsBanner();

      var variantId = null;
      if (pkg === 'framed') {
        var frame = this.chosenFrame();
        variantId = frame ? frame.id : null;
      } else if (pkg === 'priority') variantId = rp2 ? rp2.config.packages.priority : null;
      if (pkg !== 'free') {
        var v = rp2 && variantId ? rp2.variant(variantId) : null;
        if (!v || !v.available) {
          this.showOptionsBanner(this.copy.unavailable || 'This option is not available right now.');
          this.fillOptions();
          return;
        }
      }

      this.choosing = true;
      var button = this.continueButton;
      if (pkg !== 'free' && button) {
        button.disabled = true;
        button.textContent = this.copy.submitBusy || 'One moment…';
      }
      try {
        if (rp2) {
          if (pkg === 'framed') rp2.selectFrame(variantId);
          else rp2.selectPackage(pkg, { url: false });
        }
        this.track('rp2_option_chosen', { package: PACKAGE_IDS[pkg] || pkg });

        if (pkg === 'free') {
          await this.namePackage('free');
          this.freeIntake = this.lead;
          this.closeOptions();
          if (this.resume) this.resume.hidden = true;
          this.showConfirmation();
          return;
        }

        var toCheckout = this.options.dataset.afterAdd !== 'cart';
        /* Already in the cart (came back from checkout, tapped again): carry on from there. */
        if (this.added !== String(variantId)) {
          await this.namePackage(pkg);
          var cartJson;
          try {
            cartJson = await this.addToCart(variantId, this.lead, this.value('title'));
          } catch (e) {
            this.showOptionsBanner(
              this.copy.cartError || 'Your trip is saved, but it did not reach the cart. Please try again.'
            );
            return;
          }
          this.added = String(variantId);
          this.clearState();
          this.announceCart(cartJson, variantId);
        }
        if (toCheckout) {
          this.leaving = true;
          window.location.href = '/checkout';
          return;
        }
        this.closeOptions();
        this.openCart();
      } finally {
        this.choosing = false;
        if (!this.leaving) {
          if (button) button.disabled = false;
          this.fillOptions();
        }
      }
    }

    /* -------------------------- persistence ------------------------ */

    scheduleSave() {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.save(), 400);
    }

    save() {
      try {
        var state = { v: 1, fields: {}, choices: {}, stops: [], more: !!(this.more && this.more.open) };
        this.querySelectorAll('[data-tnq-input]').forEach(function (el) {
          var key = el.getAttribute('data-tnq-input');
          if (el.value) state.fields[key] = el.value;
        });
        /* Saved even while hidden, so switching packages back loses nothing. */
        ['who', 'when'].forEach((key) => {
          var checked = this.querySelector('[data-tnq-choice="' + key + '"]:checked');
          if (checked) state.choices[key] = checked.value;
        });
        this.stops.forEach(function (stop) {
          var place = stop.ac.value();
          if (place) state.stops.push(place);
        });
        if (this.gpx.state === 'done' && this.gpx.gpxId) {
          state.gpx = { gpxId: this.gpx.gpxId, name: this.gpx.name, km: this.gpx.km };
        }
        localStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch (e) {
        /* storage blocked or full: the form still works */
      }
    }

    restore() {
      var state = null;
      try {
        state = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      } catch (e) {
        return;
      }
      if (!state || state.v !== 1) return;
      try {
        Object.keys(state.fields || {}).forEach((key) => {
          var el = this.input(key);
          if (el && !el.value && typeof state.fields[key] === 'string') el.value = state.fields[key];
        });
        /* A save from before the entry point was folded into the stops. */
        var stops = (state.stops || []).slice();
        if (state.entry && state.entry.name) stops.unshift(state.entry);
        stops.forEach((place) => {
          if (place && place.name) this.addStop(place);
        });
        Object.keys(state.choices || {}).forEach((key) => {
          this.querySelectorAll('[data-tnq-choice="' + key + '"]').forEach(function (radio) {
            if (radio.value === state.choices[key]) radio.checked = true;
          });
        });
        if (state.more && this.more) this.more.open = true;
        /* The Studio honours the id for a day; past that it says so and the file is asked for again. */
        if (state.gpx && state.gpx.gpxId && this.gpxInput) {
          this.gpx.state = 'done';
          this.gpx.gpxId = String(state.gpx.gpxId);
          this.gpx.name = String(state.gpx.name || '');
          this.gpx.km = Number(state.gpx.km) || 0;
          this.setGpxStatus(this.gpxLine(), 'green');
        }
      } catch (e) {
        /* a corrupt save never blocks a fresh form */
      }
    }

    clearState() {
      clearTimeout(this.saveTimer);
      try {
        localStorage.removeItem(this.storageKey);
      } catch (e) {
        /* nothing to clear */
      }
    }
  }

  if (!customElements.get('tn-rp2-form')) customElements.define('tn-rp2-form', TnRp2Form);

})();
